import React from 'react';

const FIELD = 'w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-2 py-1.5 text-xs text-white placeholder-gray-600 focus:outline-none focus:border-blue-500';

export const emptyChampionship = () => ({ label: '', url: '', match: '', score_by: 'overall', system: 'mxgb', groups: '', top_n: 5, rounds_remaining: '', motos_per_round: 2 });

/** Only rows with a label and a URL are championships; the rest are unfinished. */
export function cleanChampionships(list) {
  return (list || [])
    .filter(c => c.label?.trim() && c.url?.trim())
    .map(c => ({
      label: c.label.trim(),
      url: c.url.trim(),
      match: (c.match || '').trim(),
      score_by: c.score_by === 'within' ? 'within' : 'overall',
      system: c.system === 'bssc' ? 'bssc' : 'mxgb',
      groups: String(c.groups || '').split(',').map(g => g.trim()).filter(Boolean),
      top_n: Number(c.top_n) || 5,
      // Blank means "not set": the key-info page then claims nothing about the title.
      rounds_remaining: c.rounds_remaining === '' || c.rounds_remaining == null ? null : Number(c.rounds_remaining),
      motos_per_round: Number(c.motos_per_round) || 2,
    }));
}

/**
 * The championships a meeting runs, one row each: a short name, where the
 * standings before today come from, which sessions count, and how a race is
 * scored. Controlled: the caller owns the list.
 */
export default function ChampionshipEditor({ value, onChange }) {
  const list = value || [];
  const set = (i, field, v) => onChange(list.map((c, n) => (n === i ? { ...c, [field]: v } : c)));
  const labels = list.map(c => c.label?.trim()).filter(Boolean);
  const duplicate = labels.find((l, i) => labels.indexOf(l) !== i);

  return (
    <div className="space-y-2">
      {list.length > 0 && (
        <div className="hidden sm:grid grid-cols-[70px_1fr_110px_120px_120px_50px_70px_60px_24px] gap-2 text-[10px] uppercase tracking-wide text-gray-500">
          <span>Name</span><span>Standings before today</span><span>Sessions with</span><span>Scored by</span><span>Points</span><span>Top</span><span title="Rounds in the season after this meeting">Rounds left</span><span>Races/rd</span><span />
        </div>
      )}
      {list.map((c, i) => (
        <React.Fragment key={i}>
        <div className="grid grid-cols-1 sm:grid-cols-[70px_1fr_110px_120px_120px_50px_70px_60px_24px] gap-2 items-center">
          <input value={c.label} onChange={e => set(i, 'label', e.target.value)} placeholder="125" className={FIELD} />
          <input value={c.url} onChange={e => set(i, 'url', e.target.value)} placeholder="Speedhive championship URL or Google Sheet" className={FIELD} />
          <input value={c.match} onChange={e => set(i, 'match', e.target.value)} placeholder="e.g. 125" className={FIELD} />
          <select value={c.score_by || 'overall'} onChange={e => set(i, 'score_by', e.target.value)} className={FIELD}>
            <option value="overall">Race position</option>
            <option value="within">Within this class</option>
          </select>
          <select value={c.system || 'mxgb'} onChange={e => set(i, 'system', e.target.value)} className={FIELD} title="How results become championship points">
            <option value="mxgb">MXGB, per race</option>
            <option value="bssc">Supermoto, per day</option>
          </select>
          <input type="number" min={1} max={40} value={c.top_n} onChange={e => set(i, 'top_n', e.target.value)} className={FIELD} />
          <input type="number" min={0} max={50} value={c.rounds_remaining ?? ''} onChange={e => set(i, 'rounds_remaining', e.target.value)} placeholder="?" title="Rounds in the season after this meeting, for 'guaranteed champion'" className={FIELD} />
          <input type="number" min={1} max={6} value={c.motos_per_round ?? 2} onChange={e => set(i, 'motos_per_round', e.target.value)} className={FIELD} />
          <button onClick={() => onChange(list.filter((_, n) => n !== i))} className="text-gray-600 hover:text-red-400 text-sm" title="Remove">×</button>
        </div>
        {c.system === 'bssc' && (
          <div className="flex items-center gap-2 sm:pl-[78px]">
            <span className="text-[10px] uppercase tracking-wide text-gray-500 whitespace-nowrap">Groups</span>
            <input
              value={c.groups || ''}
              onChange={e => set(i, 'groups', e.target.value)}
              placeholder="e.g. Group A, Group B, Group C (best first, as written in the session names)"
              className={FIELD}
            />
          </div>
        )}
        </React.Fragment>
      ))}
      <button onClick={() => onChange([...list, emptyChampionship()])} className="text-[11px] text-blue-500 hover:text-blue-400">+ Add a championship</button>
      {duplicate && <p className="text-[10px] text-amber-500">Two championships are called "{duplicate}". Names must differ; they are how a graphic picks one.</p>}
      <p className="text-[10px] text-gray-600 leading-relaxed">
        <strong className="text-gray-400">Sessions with</strong>: only sessions whose title contains this count, since other classes race in between
        (U21 counts the MX2 sessions too). <strong className="text-gray-400">Within this class</strong> scores by position among the championship's own
        riders, which is how MXGB scores U21 inside MX2. <strong className="text-gray-400">Rounds left</strong> is the rounds in the season after this
        meeting: the broadcast page needs it to say who can still win and when a champion is guaranteed, and says nothing about the title
        without it. <strong className="text-gray-400">MXGB, per race</strong>: each race scores 25-22-20-18-16-15 … 1 for 20th straight into the
        championship. <strong className="text-gray-400">Supermoto, per day</strong> (British Supermoto): each race scores 35-32-30-28-26-24-22, then one
        less a place to 1 for 28th, then 0.1 less; the day's total ranks the day and the day position earns championship points on the same scale. <strong className="text-gray-400">Groups</strong>: when a race runs in
        groups scored as one giant race, the words that name each group in the session names, best group first. Every rider in a group is placed after
        everyone in the groups above it, so B's winner is 19th behind an A group of 18.
      </p>
    </div>
  );
}
